import { useEffect, useState } from "react";

const DEFAULT_PADDING = 8;

export function measureElement(element, padding = DEFAULT_PADDING) {
  const rect = element.getBoundingClientRect();
  return {
    top: Math.max(0, rect.top - padding),
    left: Math.max(0, rect.left - padding),
    width: rect.width + padding * 2,
    height: rect.height + padding * 2,
  };
}

/**
 * Tracks a spotlight rectangle for a DOM target with resize/orientation updates.
 *
 * @param {Element | null} target
 * @param {boolean} [enabled]
 */
export function useSpotlightRect(target, enabled = true) {
  const [rect, setRect] = useState(null);

  useEffect(() => {
    if (!enabled || !target) {
      setRect(null);
      return undefined;
    }

    let frameId = null;

    const update = () => {
      if (!target.isConnected) {
        setRect(null);
        return;
      }
      setRect(measureElement(target));
    };

    const scheduleUpdate = () => {
      if (frameId) {
        window.cancelAnimationFrame(frameId);
      }
      frameId = window.requestAnimationFrame(update);
    };

    scheduleUpdate();

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(scheduleUpdate)
        : null;

    resizeObserver?.observe(target);

    window.addEventListener("resize", scheduleUpdate);
    window.addEventListener("orientationchange", scheduleUpdate);
    window.addEventListener("scroll", scheduleUpdate, true);

    return () => {
      if (frameId) {
        window.cancelAnimationFrame(frameId);
      }
      resizeObserver?.disconnect();
      window.removeEventListener("resize", scheduleUpdate);
      window.removeEventListener("orientationchange", scheduleUpdate);
      window.removeEventListener("scroll", scheduleUpdate, true);
    };
  }, [target, enabled]);

  return rect;
}

/**
 * Tracks spotlight rectangles for multiple DOM targets (visual-only regions).
 *
 * @param {(Element | null)[]} targets
 * @param {boolean} [enabled]
 * @returns {import('./spotlightTypes').SpotlightRect[]}
 */
export function useSpotlightRects(targets, enabled = true) {
  const [rects, setRects] = useState([]);

  useEffect(() => {
    if (!enabled) {
      setRects([]);
      return undefined;
    }

    const connectedTargets = (targets || []).filter((target) => target?.isConnected);
    if (!connectedTargets.length) {
      setRects([]);
      return undefined;
    }

    let frameId = null;

    const update = () => {
      const measured = (targets || [])
        .filter((target) => target?.isConnected)
        .map((target) => measureElement(target));
      setRects(measured);
    };

    const scheduleUpdate = () => {
      if (frameId) {
        window.cancelAnimationFrame(frameId);
      }
      frameId = window.requestAnimationFrame(update);
    };

    scheduleUpdate();

    const resizeObservers = connectedTargets.map((target) => {
      if (typeof ResizeObserver === "undefined") {
        return null;
      }
      const observer = new ResizeObserver(scheduleUpdate);
      observer.observe(target);
      return observer;
    });

    window.addEventListener("resize", scheduleUpdate);
    window.addEventListener("orientationchange", scheduleUpdate);
    window.addEventListener("scroll", scheduleUpdate, true);

    return () => {
      if (frameId) {
        window.cancelAnimationFrame(frameId);
      }
      resizeObservers.forEach((observer) => observer?.disconnect());
      window.removeEventListener("resize", scheduleUpdate);
      window.removeEventListener("orientationchange", scheduleUpdate);
      window.removeEventListener("scroll", scheduleUpdate, true);
    };
  }, [targets, enabled]);

  return rects;
}
